import { Separator } from '@/components/_shadcn-ui/separator';
import { handleDomBubbleEvent } from '@/utils/dom';
import {
  BadgeCheck,
  Calendar,
  CalendarPlus,
  Grid2x2X,
  MessagesSquare,
  Rocket,
  SquareCheckBig,
  Tags,
  Users,
} from 'lucide-react';
import NewFileUpload from '@/features/_shared/components/file-handler/new-file-upload';
import { formatDateRange } from '@/features/ticketings/tickets/ticket/utils/ticket.utils';
import { TUpdateTicketValidation } from '@/features/ticketings/tickets/ticket/api/update-ticket.api';
import MultiselectAutoComplete from '@/components/ui/_r/inputs/multiselect-autocomplete';
import { useUpdateTicketModal } from './use-update-ticket-modal';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/_r/react-hook-form/base-form';
import MessageError from '@/components/errors/message-error';
import Input from '@/components/ui/_r/inputs/input';
import { CaseOption, SelectedCaseItem } from '@/features/ticketing/tickets/components/modals/_shared/case-option';
import {
  LabelOption,
  SelectedLabelItem,
} from '@/features/ticketing/tickets/components/modals/_shared/label-ticket-option';
import {
  MemberOption,
  SelectedMemberItem,
} from '@/features/ticketing/tickets/components/modals/_shared/ticket-member-option';
import ShareIcon from '@/components/icons/share-icon';
import LoadingSpinner2 from '@/components/ui/new-loading-spinner';
import TicketActivityTab from '@/features/ticketings/tickets/ticket-activity/components/ticket-activity-tab/ticket-activity-tab';
import { TicketComment } from '@/features/ticketing/ticket-comments/components/ticket-comment';
import { Spinner } from '@/components/ui/_refactor/skeleton/spinner';
import { IconButton } from '@/components/ui/_refactor/buttons/icon-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/_shadcn-ui/tooltip';
import Autocomplete from '@/components/ui/_r/inputs/autocomplete';
import { cn } from '@/utils/cn';
import { AutoExpandTextarea } from '@/components/ui/_refactor/form/textareas/autoexpand-textarea';
import { AppTooltip } from '@/components/ui/_refactor/popup/tooltip/app-tooltip';
import { memo, useCallback, useState } from 'react';

type UpdateTicketFormProps = ReturnType<typeof useUpdateTicketModal>;

const MobileUpdateTicketForm = memo((props: UpdateTicketFormProps) => {
  const {
    // Tabs
    tabs,

    // Form
    createdAt,
    form,
    handleSubmitForm,
    isLoadingUpdateTicket,
    hasFormChanges,
    initialIsCompleted,
    isButtonDisabled,

    // Date modal
    dateValues,
    handleOpenDateModal,

    // Ticket members
    listTicketMemberOptions,
    isLoadingListTicketMemberData,

    // Cases
    listCaseOptions,
    isLoadingListCaseData,

    // Spaces
    listSpacesOptions,
    isLoadingListSpacesData,

    // Space columns
    listSpaceColumnsOptions,
    isLoadingListSpaceColumnsData,

    // Share
    handleShareTicket,

    // Label tickets
    listLabelTicketOptions,
    isLoadingListLabelTicketData,

    // Files
    extractedAttachments,
    uploadingFileIds,
    isUploadingFiles,
    handleAddFiles,
    handleRemoveFile,

    // Handlers
    handleMarkAsCompleteTicket,

    // IDs
    ticketId,
  } = props;

  const [activeTab, setActiveTab] = useState<(typeof tabs)[number]['value']>('details');

  const handleTabChange = useCallback((value: (typeof tabs)[number]['value']) => {
    setActiveTab(value);
  }, []);

  return (
    <div className="pt-[56px] ">
      <div className="flex items-center justify-between px-[16px]">
        <div className="flex flex-col gap-[8px]">
          <p className="text-[16px] font-[700] text-[#0C8DBF]">Update Ticket</p>
          <p className="text-[12px] font-[400] text-[#9D9D9C]">
            Update ticket for your team. Set the title, date, member, and other details to keep work organized.
          </p>
        </div>
        <div className="flex items-center gap-[8px]">
          <Tooltip>
            <TooltipTrigger asChild>
              <IconButton
                type="button"
                onClick={(e) => {
                  handleDomBubbleEvent(e);
                  handleShareTicket();
                }}
                className="rounded-[8px] border-1 border-[#1F445A] group hover:bg-[#1F445A] cursor-pointer"
                variant="ghost"
              >
                <ShareIcon className="w-[16px] h-[16px] flex-shrink-0 text-[#1F445A] group-hover:text-white" />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent className="z-[1000] bg-white text-[#25151E] text-[12px] font-normal p-2 rounded-lg border border-[#D9D9D9]">
              <p>Share Ticket</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <IconButton
                disabled={isButtonDisabled}
                type="button"
                variant="ghost"
                onClick={(e) => {
                  handleMarkAsCompleteTicket(e);
                }}
                className={cn(
                  'rounded-[8px] bg-white border-1 border-[#1F445A] group hover:bg-[#505355] cursor-pointer transition-colors duration-200',
                  {
                    'border-[#0B8A00]   bg-[#0B8A00]  hover:bg-[#0B8A00]/90': Boolean(form.watch('isCompleted')),
                    'cursor-not-allowed opacity-50 text-[#505355] bg-[#505355] hover:bg-[#505355] border-[#505355] disabled:cursor-not-allowed disabled:hover:bg-[#505355]':
                      isButtonDisabled,
                  }
                )}
              >
                <SquareCheckBig
                  className={cn(
                    'w-[16px] h-[16px] flex-shrink-0 text-[#1F445A] group-hover:text-white transition-colors duration-200',
                    {
                      'text-white': form.watch('isCompleted'),
                    }
                  )}
                />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent className="z-[1000] bg-white text-[#25151E] text-[12px] font-normal p-2 rounded-lg border border-[#D9D9D9]">
              {form.watch('isCompleted') ? <p>Ticket has been marked as completed</p> : <p>Mark as Completed Ticket</p>}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      <Separator className="mt-[10px]" />

      <MobileCreateTicketTabs items={tabs} activeValue={activeTab} onChange={handleTabChange} />

      <div className=" max-h-[60vh] overflow-y-auto">
        {activeTab === 'details' && (
          <div className="mt-[12px]">
            <Form {...form}>
              <form
                id="update-ticketing-space-column-form"
                className="h-fit"
                onSubmit={(e) => {
                  handleDomBubbleEvent(e);
                  form.handleSubmit(handleSubmitForm as (values: TUpdateTicketValidation) => void)(e);
                }}
              >
                {initialIsCompleted ? (
                  <div className="bg-[#A8E6A3] text-[#0B8A00] px-[8px] py-[6px] rounded-[4px] text-[14px] font-[400] flex items-center gap-[6px] mx-[16px] mb-[12px]">
                    <BadgeCheck className="w-[24px] h-[24px] flex-shrink-0 text-[#0B8A00]" /> This ticket has been
                    completed. You can only edit its position.
                  </div>
                ) : null}
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => {
                    const error = form?.formState?.errors?.title?.message || '';
                    const value = field?.value || '';
                    const disabled = Boolean(initialIsCompleted);

                    return (
                      <FormItem>
                        <div className="px-[16px]">
                          <FormControl>
                            <AutoExpandTextarea
                              {...field}
                              value={value}
                              readOnly={disabled}
                              placeholder="Enter a title for this card..."
                              disabled={disabled}
                              className={cn(
                                'text-[#25151E] text-[24px] font-[700] w-full bg-transparent border-0 border-b-[2px] border-transparent focus:border-[#0C8DBF] focus:outline-none focus:ring-0 px-0 py-[0px] transition-colors duration-200 placeholder:text-[#9D9D9C] placeholder:font-[400] resize-none overflow-hidden rounded-none min-h-0 leading-tight p-0',
                                {
                                  'cursor-not-allowed': disabled,
                                }
                              )}
                            />
                          </FormControl>
                          <MessageError message={error} />
                        </div>
                      </FormItem>
                    );
                  }}
                />

                <div className="mt-[20px] space-y-[10px] px-[16px]">
                  <FormField
                    control={form.control}
                    name="spaceId"
                    render={({ field }) => {
                      const disabled = true || Boolean(initialIsCompleted);
                      const error = form?.formState?.errors?.spaceId?.message || '';
                      const value = listSpacesOptions.find((option) => option.spaceId === field.value) || null;

                      return (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel className="flex items-center gap-[8px]">
                              <Rocket className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                              <span className="text-[12px] font-[700] text-[#4B4B4B]">Space</span>
                            </FormLabel>
                            <div className="max-w-[220px] w-full">
                              <FormControl>
                                <Autocomplete
                                  labelKey="name"
                                  valueKey="spaceId"
                                  placeholder="Select spaces"
                                  disabled={disabled}
                                  value={value}
                                  options={listSpacesOptions}
                                  isLoading={isLoadingListSpacesData}
                                  onChange={(selectedValue) => {
                                    field.onChange(selectedValue?.spaceId || '');
                                  }}
                                  onClear={() => {
                                    form.setValue('spaceId', '', { shouldValidate: true });
                                    form.setValue('spaceColumnId', '', { shouldValidate: true });
                                  }}
                                />
                              </FormControl>
                            </div>
                          </div>
                          <MessageError message={error} />
                        </FormItem>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="spaceColumnId"
                    render={({ field }) => {
                      const disabled =
                        !form.watch('spaceId') || isLoadingListSpaceColumnsData || isLoadingListSpacesData;
                      const error = form?.formState?.errors?.spaceColumnId?.message || '';
                      const value =
                        listSpaceColumnsOptions.find((option) => option.spaceColumnId === field.value) || null;

                      return (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel className="flex items-center gap-[8px]">
                              <Grid2x2X className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                              <span className="text-[12px] font-[700] text-[#4B4B4B]">Column</span>
                            </FormLabel>
                            <div className="max-w-[220px] w-full">
                              <FormControl>
                                <Autocomplete
                                  labelKey="name"
                                  valueKey="spaceColumnId"
                                  placeholder="Select columns"
                                  value={value}
                                  disabled={disabled}
                                  isLoading={isLoadingListSpaceColumnsData}
                                  options={listSpaceColumnsOptions}
                                  onChange={(selectedValue) => {
                                    field.onChange(selectedValue?.spaceColumnId ?? '');
                                  }}
                                  onClear={() => {
                                    form.setValue('spaceColumnId', '', { shouldValidate: true });
                                  }}
                                />
                              </FormControl>
                            </div>
                          </div>
                          <MessageError message={error} />
                        </FormItem>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field: _field }) => {
                      const disabled = Boolean(initialIsCompleted);
                      const error =
                        form?.formState?.errors?.startDate?.message || form?.formState?.errors?.endDate?.message || '';

                      return (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel className="flex items-center gap-[8px]">
                              <Calendar className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                              <span className="text-[12px] font-[700] text-[#4B4B4B]">Dates</span>
                            </FormLabel>
                            <div className="max-w-[220px] w-full">
                              <FormControl>
                                <Tooltip>
                                  <TooltipTrigger asChild disabled={disabled}>
                                    <Input
                                      placeholder="Set Due date"
                                      value={formatDateRange(dateValues)}
                                      type="text"
                                      readOnly
                                      disabled={disabled}
                                      className={cn('cursor-pointer disabled:text-[#9D9D9C]', {
                                        'cursor-not-allowed': disabled,
                                      })}
                                      onClick={() => {
                                        if (!disabled) {
                                          handleOpenDateModal();
                                        }
                                      }}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent className="z-[100001] bg-white text-[#25151E] text-[12px] font-normal p-2 rounded-lg border">
                                    <span>{formatDateRange(dateValues) || 'Set Due date'}</span>
                                  </TooltipContent>
                                </Tooltip>
                              </FormControl>
                            </div>
                          </div>
                          <MessageError message={error} />
                        </FormItem>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="caseIds"
                    render={({ field }) => {
                      const error = form?.formState?.errors?.caseIds?.message || '';
                      const value = listCaseOptions.filter((option) => field.value?.includes(option.caseId));
                      const disabled = isLoadingListCaseData || Boolean(initialIsCompleted);

                      return (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel className="flex items-center gap-[8px]">
                              <MessagesSquare className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                              <span className="text-[12px] font-[700] text-[#4B4B4B]">Case</span>
                            </FormLabel>
                            <div className="max-w-[220px] w-full">
                              <FormControl>
                                <MultiselectAutoComplete
                                  labelKey="name"
                                  valueKey="caseId"
                                  placeholder="Select case"
                                  searchPlaceholder="Search case"
                                  options={listCaseOptions}
                                  value={value}
                                  disabled={disabled}
                                  isLoading={isLoadingListCaseData}
                                  onChange={(value) => {
                                    field.onChange(value.map((option) => option.caseId));
                                  }}
                                  onClearAll={() => {
                                    field.onChange([]);
                                  }}
                                  renderOption={(option) => <CaseOption option={option} />}
                                  renderSelectedItem={(option, disabled) => (
                                    <SelectedCaseItem
                                      option={option}
                                      onRemove={(option) => {
                                        if (option && !disabled) {
                                          field.onChange(
                                            field.value?.filter((id: string) => id !== option.caseId) || []
                                          );
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </FormControl>
                            </div>
                          </div>
                          <MessageError message={error} />
                        </FormItem>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="userIds"
                    render={({ field }) => {
                      const error = form?.formState?.errors?.userIds?.message || '';
                      const value = listTicketMemberOptions.filter((option) => field.value?.includes(option.userId));
                      const disabled = isLoadingListTicketMemberData || Boolean(initialIsCompleted);

                      return (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel className="flex items-center gap-[8px]">
                              <Users className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                              <span className="text-[12px] font-[700] text-[#4B4B4B]">Member</span>
                            </FormLabel>
                            <div className="max-w-[220px] w-full">
                              <FormControl>
                                <MultiselectAutoComplete
                                  labelKey="fullname"
                                  valueKey="userId"
                                  placeholder="Select members"
                                  searchPlaceholder="Search members"
                                  disabled={disabled}
                                  isLoading={isLoadingListTicketMemberData}
                                  options={listTicketMemberOptions}
                                  value={value}
                                  onChange={(value) => {
                                    field.onChange(value.map((option) => option.userId));
                                  }}
                                  onClearAll={() => {
                                    field.onChange([]);
                                  }}
                                  renderOption={(option) => <MemberOption option={option} />}
                                  renderSelectedItem={(option, disabled) => (
                                    <SelectedMemberItem
                                      option={option}
                                      onRemove={(option) => {
                                        if (option && !disabled) {
                                          field.onChange(
                                            field.value?.filter((id: string) => id !== option.userId) || []
                                          );
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </FormControl>
                            </div>
                          </div>
                          <MessageError message={error} />
                        </FormItem>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="labelTicketIds"
                    render={({ field }) => {
                      const error = form?.formState?.errors?.labelTicketIds?.message || '';
                      const value = listLabelTicketOptions.filter((option) =>
                        field.value?.includes(option.labelTicketId)
                      );
                      const disabled = isLoadingListLabelTicketData || Boolean(initialIsCompleted);

                      return (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel className="flex items-center gap-[8px]">
                              <Tags className="w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]" />
                              <span className="text-[12px] font-[700] text-[#4B4B4B]">Label</span>
                            </FormLabel>
                            <div className="max-w-[220px] w-full">
                              <FormControl>
                                <MultiselectAutoComplete
                                  labelKey="name"
                                  valueKey="labelTicketId"
                                  placeholder="Select labels"
                                  searchPlaceholder="Search labels"
                                  disabled={disabled}
                                  isLoading={isLoadingListLabelTicketData}
                                  options={listLabelTicketOptions}
                                  value={value}
                                  onChange={(value) => {
                                    field.onChange(value.map((option) => option.labelTicketId));
                                  }}
                                  onClearAll={() => {
                                    field.onChange([]);
                                  }}
                                  renderOption={(option) => <LabelOption option={option} />}
                                  renderSelectedItem={(option, disabled) => (
                                    <SelectedLabelItem
                                      option={option}
                                      onRemove={(option) => {
                                        if (option && !disabled) {
                                          field.onChange(
                                            field.value?.filter((id: string) => id !== option.labelTicketId) || []
                                          );
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </FormControl>
                            </div>
                          </div>
                          <MessageError message={error} />
                        </FormItem>
                      );
                    }}
                  />
                  <div className="w-fit">
                    <AppTooltip content={`Created At: ${createdAt}`} contentClassName="z-[100001]">
                      <div className="flex items-center gap-[8px] cursor-pointer" tabIndex={0} role="button">
                        <CalendarPlus className="w-[16px] h-[16px] text-[#9D9D9C] flex-shrink-0" />{' '}
                        <span className="text-[12px] font-[700] text-[#4B4B4B] block">{createdAt}</span>
                      </div>
                    </AppTooltip>
                  </div>
                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => {
                      const error = form?.formState?.errors?.content?.message || '';
                      const value = field?.value || '';
                      const disabled = Boolean(initialIsCompleted);

                      return (
                        <FormItem>
                          <div className="space-y-[8px] mt-[16px]">
                            <FormLabel className="flex items-center gap-[8px]">
                              <span className="text-[12px] font-[700] text-[#4B4B4B]">Content</span>{' '}
                              <span className="text-[12px] font-[400] text-[#9D9D9C]">(Optional)</span>
                            </FormLabel>
                            <FormControl>
                              <AutoExpandTextarea
                                {...field}
                                value={value}
                                placeholder="Enter ticket details..."
                                minHeight={100}
                                disabled={disabled}
                                className="w-full rounded-[8px] border border-[#D9D9D9] p-[8px] text-[13px] disabled:cursor-not-allowed disabled:opacity-50"
                              />
                            </FormControl>
                          </div>
                          <MessageError message={error} />
                        </FormItem>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="attachments"
                    render={({ field: _field }) => {
                      const error = form?.formState?.errors?.attachments?.message || '';
                      const disabled = Boolean(initialIsCompleted);

                      return (
                        <FormItem>
                          <div className="space-y-[8px] mt-[8px]">
                            <FormLabel className="flex items-center gap-[8px]">
                              <span className="text-[12px] font-[700] text-[#4B4B4B]">Attachments</span>{' '}
                              <span className="text-[12px] font-[400] text-[#9D9D9C]">(Optional)</span>
                            </FormLabel>
                            <FormControl>
                              <div className={cn({ 'pointer-events-none opacity-50': disabled })}>
                                <NewFileUpload
                                  accept="*"
                                  maxSize={20 * 1024 * 1024} // 20MB
                                  maxFiles={10}
                                  initialFiles={extractedAttachments || []}
                                  isUploadingFiles={isUploadingFiles}
                                  uploadingFileIds={uploadingFileIds}
                                  onFilesAdded={(files) => {
                                    if (!disabled) {
                                      handleAddFiles(files);
                                    }
                                  }}
                                  onFileRemoved={(removedFile) => {
                                    if (!disabled) {
                                      handleRemoveFile(removedFile);
                                    }
                                  }}
                                />
                              </div>
                            </FormControl>
                          </div>
                          <MessageError message={error} />
                        </FormItem>
                      );
                    }}
                  />
                </div>

                <div className="flex justify-end mt-[24px] px-[16px] pb-[16px]">
                  <button
                    disabled={isLoadingUpdateTicket || isUploadingFiles || !hasFormChanges}
                    form="update-ticketing-space-column-form"
                    type="submit"
                    className="w-fit bg-[#0C8DBF] cursor-pointer disabled:cursor-not-allowed disabled:bg-[#9D9D9C] text-[14px] text-[#FFF] font-[700] rounded-[8px] py-[8px] px-[16px] flex items-center gap-[4px]"
                  >
                    {isLoadingUpdateTicket ? <LoadingSpinner2 /> : null} Save Changes
                  </button>
                </div>
              </form>
            </Form>
          </div>
        )}
        {activeTab === 'comments' && (
          <div className="h-[400px] ">
            <TicketComment ticketId={ticketId} listTicketMember={listTicketMemberOptions} />
          </div>
        )}
        {activeTab === 'activity' && (
          <div className="h-[400px]  bg-[#F0F0F0] overflow-y-auto">
            <TicketActivityTab ticketId={ticketId} />
          </div>
        )}
      </div>
    </div>
  );
});
MobileUpdateTicketForm.displayName = 'MobileUpdateTicketForm';

export default MobileUpdateTicketForm;

type MobileCreateTicketTabItem<T extends string> = {
  label: string;
  value: T;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  count?: number;
  isLoading?: boolean;
};

type MobileCreateTicketTabsProps<T extends string> = {
  items: MobileCreateTicketTabItem<T>[];
  activeValue: T;
  onChange: (value: T) => void;
};

function MobileCreateTicketTabs<T extends string>({ items, activeValue, onChange }: MobileCreateTicketTabsProps<T>) {
  return (
    <div className="flex flex-row border-b border-[#E2E8F0] px-[6px]">
      {items.map((item) => {
        const isActive = item.value === activeValue;
        const Icon = item.icon;

        return (
          <button
            key={item.value}
            type="button"
            onClick={() => onChange(item.value)}
            className={cn('flex flex-1 flex-row items-center justify-center gap-[6px] py-[6px]', {
              'border-b-[2px] border-[#0C8DBF]': isActive,
              'border-b-[2px] border-transparent': !isActive,
            })}
          >
            {Icon ? (
              <Icon
                className={cn('w-[16px] h-[16px] flex-shrink-0 text-[#9D9D9C]', {
                  'text-[#0C8DBF]': isActive,
                  'text-[#9D9D9C]': !isActive,
                })}
              />
            ) : null}
            <span
              className={cn('text-[12px] font-[700] text-[#9D9D9C]', {
                'text-[#0C8DBF]': isActive,
                'text-[#9D9D9C]': !isActive,
              })}
            >
              {item.label}
            </span>
            {item.count && item.count > 0 && !item.isLoading ? (
              <div
                className={cn(
                  'rounded-full cursor-pointer text-[11px] w-5 h-5 flex items-center justify-center bg-[#E4F8FF] text-[#0C8DBF]',
                  {
                    'bg-gray-200 text-gray-500': item.isLoading,
                    'bg-[#E4F8FF] text-[#0C8DBF]': !item.isLoading,
                  }
                )}
              >
                {item.count || 0}
              </div>
            ) : null}
            {item.isLoading ? (
              <Spinner
                isLoading={item.isLoading}
                className="w-3 h-3 border-[1.5px]"
                borderColor="border-gray-200"
                borderTopColor="border-t-[#0C8DBF]"
                loadingBorderTopColor="border-t-[#0C8DBF]"
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
